import React from "react";
import {
  Bell,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  FileText,
  MessageSquareText,
  Ticket,
  UserRound,
  Video,
} from "lucide-react";
import EventNotification from "../../interfaces/eventNotification/EventNotification";

export type NotificationCategory =
  | "all"
  | "unread"
  | "tasks"
  | "comments"
  | "files"
  | "meetings"
  | "other";

export interface NotificationCategoryDefinition {
  id: NotificationCategory;
  label: string;
  shortLabel: string;
}

export const NOTIFICATION_CATEGORIES: NotificationCategoryDefinition[] = [
  { id: "all", label: "All notifications", shortLabel: "All" },
  { id: "unread", label: "Unread notifications", shortLabel: "Unread" },
  { id: "tasks", label: "Tasks", shortLabel: "Tasks" },
  { id: "comments", label: "Comments and activity", shortLabel: "Comments" },
  { id: "files", label: "Files", shortLabel: "Files" },
  { id: "meetings", label: "Meetings", shortLabel: "Meetings" },
  { id: "other", label: "Other notifications", shortLabel: "Other" },
];

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? "";

export const getNotificationCategory = (
  notification: EventNotification,
): Exclude<NotificationCategory, "all" | "unread"> => {
  const type = normalize(notification.type);
  const resourceTypes = new Set(
    notification.notificationResources?.map((resource) =>
      normalize(resource.resourceType),
    ) ?? [],
  );

  const taskActionResource = notification.notificationResources?.find(
    (resource) => normalize(resource.resourceType) === "task_action",
  );
  const taskActionName = normalize(taskActionResource?.resourceName);

  if (
    resourceTypes.has("meeting") ||
    type.includes("meeting") ||
    type.includes("calendar")
  ) {
    return "meetings";
  }

  if (
    resourceTypes.has("file") ||
    resourceTypes.has("FILE".toLowerCase()) ||
    type.includes("file") ||
    type.includes("document")
  ) {
    return "files";
  }

  if (
    resourceTypes.has("task_action") ||
    taskActionName.includes("comment") ||
    taskActionName.includes("activity") ||
    type.includes("comment") ||
    type.includes("mention") ||
    type.includes("activity") ||
    type.includes("chat")
  ) {
    return "comments";
  }

  if (
    resourceTypes.has("task") ||
    type.includes("task") ||
    type.includes("approval") ||
    type.includes("extension") ||
    type.includes("delay") ||
    type.includes("workflow")
  ) {
    return "tasks";
  }

  return "other";
};

export const matchesNotificationCategory = (
  notification: EventNotification,
  category: NotificationCategory,
): boolean => {
  if (category === "all") return true;
  if (category === "unread") return !notification.read;
  return getNotificationCategory(notification) === category;
};

export const getNotificationCategoryLabel = (
  notification: EventNotification,
): string => {
  if (
    notification.notificationResources?.some(
      (resource) => normalize(resource.resourceType) === "wiki_page",
    )
  ) {
    return "FAQs";
  }

  const category = getNotificationCategory(notification);
  return (
    NOTIFICATION_CATEGORIES.find((item) => item.id === category)?.shortLabel ??
    "Other"
  );
};

export const getNotificationIcon = (
  notification: EventNotification,
  size = 16,
): React.ReactNode => {
  const category = getNotificationCategory(notification);

  const commonProps = {
    size,
    strokeWidth: 2,
  };

  switch (category) {
    case "tasks":
      return <CheckCircle2 {...commonProps} />;
    case "comments":
      return <MessageSquareText {...commonProps} />;
    case "files":
      return <FileText {...commonProps} />;
    case "meetings":
      return <Video {...commonProps} />;
    case "other": {
      const type = normalize(notification.type);
      if (
        notification.notificationResources?.some(
          (resource) => normalize(resource.resourceType) === "wiki_page",
        )
      ) {
        return <BookOpen {...commonProps} />;
      }
      if (type.includes("ticket")) return <Ticket {...commonProps} />;
      if (type.includes("user")) return <UserRound {...commonProps} />;
      if (
        notification.status === "error" ||
        notification.status === "warning"
      ) {
        return <CircleAlert {...commonProps} />;
      }
      return <Bell {...commonProps} />;
    }
    default:
      return <Bell {...commonProps} />;
  }
};

export const getNotificationIconClasses = (
  notification: EventNotification,
): string => {
  const category = getNotificationCategory(notification);

  switch (category) {
    case "tasks":
      return "bg-emerald-50 dark:bg-emerald-400/10 text-emerald-700 dark:text-emerald-300 border-emerald-100";
    case "comments":
      return "bg-purple-50 dark:bg-purple-400/10 text-[#7E57C2] dark:text-purple-300 border-purple-100";
    case "files":
      return "bg-sky-50 dark:bg-sky-400/10 text-sky-700 dark:text-sky-300 border-sky-100";
    case "meetings":
      return "bg-amber-50 dark:bg-amber-400/10 text-amber-700 dark:text-amber-300 border-amber-100";
    default:
      if (
        notification.notificationResources?.some(
          (resource) => normalize(resource.resourceType) === "wiki_page",
        )
      ) {
        return "bg-purple-50 dark:bg-purple-400/10 text-[#7E57C2] dark:text-purple-300 border-purple-100";
      }
      return notification.status === "error"
        ? "bg-red-50 dark:bg-red-400/10 text-red-700 dark:text-red-300 border-red-100"
        : notification.status === "warning"
          ? "bg-orange-50 dark:bg-orange-400/10 text-orange-700 dark:text-orange-300 border-orange-100"
          : "bg-gray-50 dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 border-gray-100";
  }
};

export type NotificationDateGroup =
  | "today"
  | "yesterday"
  | "this_week"
  | "earlier";

export interface GroupedNotifications {
  id: NotificationDateGroup;
  label: string;
  notifications: EventNotification[];
}

const startOfDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

export const groupNotificationsByDate = (
  notifications: EventNotification[],
): GroupedNotifications[] => {
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const startOfWeekWindow = new Date(today);
  startOfWeekWindow.setDate(startOfWeekWindow.getDate() - 6);

  const groups: GroupedNotifications[] = [
    { id: "today", label: "Today", notifications: [] },
    { id: "yesterday", label: "Yesterday", notifications: [] },
    { id: "this_week", label: "Earlier this week", notifications: [] },
    { id: "earlier", label: "Older", notifications: [] },
  ];

  notifications.forEach((notification) => {
    const createdAt = new Date(notification.createdAt);

    if (createdAt >= today) {
      groups[0].notifications.push(notification);
    } else if (createdAt >= yesterday) {
      groups[1].notifications.push(notification);
    } else if (createdAt >= startOfWeekWindow) {
      groups[2].notifications.push(notification);
    } else {
      groups[3].notifications.push(notification);
    }
  });

  return groups.filter((group) => group.notifications.length > 0);
};

export const sortAndDedupeNotifications = (
  notifications: EventNotification[],
): EventNotification[] =>
  Array.from(
    new Map(notifications.map((notification) => [notification.id, notification])).values(),
  ).sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

export const getCategoryCount = (
  notifications: EventNotification[],
  category: NotificationCategory,
): number =>
  notifications.filter((notification) =>
    matchesNotificationCategory(notification, category),
  ).length;

export const NotificationCategoryIcon: React.FC<{
  notification: EventNotification;
  compact?: boolean;
}> = ({ notification, compact = false }) => (
  <div
    className={[
      "dark-borderless flex shrink-0 items-center justify-center rounded-xl border",
      compact ? "h-8 w-8" : "h-10 w-10",
      getNotificationIconClasses(notification),
    ].join(" ")}
    aria-hidden="true"
  >
    {getNotificationIcon(notification, compact ? 15 : 18)}
  </div>
);

export const CalendarGroupIcon = () => (
  <CalendarDays size={13} className="text-gray-400" aria-hidden="true" />
);


export type NotificationAggregateKind =
  | "task_approved"
  | "task_rejected"
  | "task_assigned"
  | "task_updated"
  | "comment"
  | "file"
  | "meeting"
  | "other";

export interface NotificationAggregateGroup {
  id: string;
  kind: NotificationAggregateKind;
  label: string;
  notifications: EventNotification[];
  newestCreatedAt: Date;
  unreadCount: number;
  isAggregate: boolean;
}

const getNotificationAggregateKind = (
  notification: EventNotification,
): NotificationAggregateKind => {
  const haystack = [
    notification.type,
    notification.status,
    notification.title,
    notification.message,
    ...(notification.notificationResources?.map(
      (resource) => `${resource.resourceType} ${resource.resourceName}`,
    ) ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    haystack.includes("approved") ||
    haystack.includes("approval completed") ||
    haystack.includes("task approval")
  ) {
    return "task_approved";
  }

  if (haystack.includes("rejected") || haystack.includes("declined")) {
    return "task_rejected";
  }

  if (
    haystack.includes("assigned") ||
    haystack.includes("assignment") ||
    haystack.includes("assignee")
  ) {
    return "task_assigned";
  }

  if (
    haystack.includes("comment") ||
    haystack.includes("mention") ||
    haystack.includes("chat")
  ) {
    return "comment";
  }

  const category = getNotificationCategory(notification);

  if (category === "files") return "file";
  if (category === "meetings") return "meeting";
  if (category === "tasks") return "task_updated";

  return "other";
};

const getAggregateLabel = (
  kind: NotificationAggregateKind,
  count: number,
): string => {
  const plural = count === 1 ? "" : "s";

  switch (kind) {
    case "task_approved":
      return `You had ${count} task${plural} approved`;
    case "task_rejected":
      return `You had ${count} task${plural} rejected`;
    case "task_assigned":
      return `${count} task${plural} assigned to you`;
    case "task_updated":
      return `${count} task update${plural}`;
    case "comment":
      return `${count} new comment${plural} or mention${plural}`;
    case "file":
      return `${count} file notification${plural}`;
    case "meeting":
      return `${count} meeting notification${plural}`;
    default:
      return `${count} notification${plural}`;
  }
};

export const aggregateNotifications = (
  notifications: EventNotification[],
  minimumGroupSize = 3,
): NotificationAggregateGroup[] => {
  const byKind = new Map<NotificationAggregateKind, EventNotification[]>();

  notifications.forEach((notification) => {
    const kind = getNotificationAggregateKind(notification);
    const existing = byKind.get(kind) ?? [];
    existing.push(notification);
    byKind.set(kind, existing);
  });

  const groups: NotificationAggregateGroup[] = [];

  byKind.forEach((items, kind) => {
    const sorted = sortAndDedupeNotifications(items);

    if (sorted.length >= minimumGroupSize && kind !== "other") {
      groups.push({
        id: `aggregate-${kind}-${sorted[0]?.id ?? "unknown"}`,
        kind,
        label: getAggregateLabel(kind, sorted.length),
        notifications: sorted,
        newestCreatedAt: new Date(sorted[0].createdAt),
        unreadCount: sorted.filter((item) => !item.read).length,
        isAggregate: true,
      });

      return;
    }

    sorted.forEach((notification) => {
      groups.push({
        id: `single-${notification.id}`,
        kind,
        label: notification.title || "Notification",
        notifications: [notification],
        newestCreatedAt: new Date(notification.createdAt),
        unreadCount: notification.read ? 0 : 1,
        isAggregate: false,
      });
    });
  });

  return groups.sort(
    (a, b) => b.newestCreatedAt.getTime() - a.newestCreatedAt.getTime(),
  );
};
